import { useMutation, useQuery } from '@tanstack/react-query'

import { convertObjectToCamelCase } from '@/helpers'
import {
  IAConversationProps,
  IAConversationListWithPagesProps,
  IAConversationMetricsProps,
} from '@/types'

import { QUERIES_ADMIN } from './api.constants'
import { api } from './api.service'

import {
  AI_CONVERSATIONS_URL,
  AI_CONVERSATION_URL,
  AI_CONVERSATIONS_EVALUATION_URL,
  AI_CONVERSATIONS_EVALUATION_ALL_URL,
  AI_CONVERSATION_EMAIL_URL,
  AI_CONVERSATIONS_FORCE_FINISHED_URL,
  AI_CONVERSATION_METRICS_URL,
  AI_DASHBOARD_MONTHLY_METRICS_URL,
  AI_CONVERSATIONS_WITH_AGENT_URL,
} from './api.urls'

export const useAIConversationList = ({
  params,
  isEnabled = true,
}: {
  params?: Record<string, string | number>
  isEnabled?: boolean
}) => {
  const getConversationList = async (): Promise<{
    success: boolean
    message: string
    data?: IAConversationListWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key]) {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(AI_CONVERSATIONS_URL, {
        params: {
          ...(formattedParams as Record<string, string>),
        },
      })

      return {
        success: true,
        message: 'Conversations fetched successfully!',
        data: convertObjectToCamelCase(
          data,
        ) as IAConversationListWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get conversations!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.AI_CONVERSATION.module, params],
    queryFn: () => getConversationList(),
    enabled: isEnabled,
  })
}

export const useAIConversationDetail = (
  uuid?: string,
  email?: string,
  configs?: {
    refetchInterval?: number
  },
) => {
  const getConversationDetail = async (): Promise<{
    success: boolean
    message: string
    data?: IAConversationProps
  }> => {
    try {
      const { data } = await api.get(
        uuid ? AI_CONVERSATION_URL(uuid) : AI_CONVERSATION_EMAIL_URL(email),
      )

      return {
        success: true,
        message: 'Conversation fetched successfully!',
        data: convertObjectToCamelCase(data) as IAConversationProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get conversation!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.AI_CONVERSATION.module, uuid],
    queryFn: () => getConversationDetail(),
    enabled: !!uuid || !!email,
    ...configs,
  })
}

export const useAICreateConversation = () => {
  const createConversation = async (
    payload?: Partial<IAConversationProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: IAConversationProps
  }> => {
    try {
      const { data } = await api.post(AI_CONVERSATIONS_URL, payload)

      return {
        success: true,
        message: 'Conversation created successfully!',
        data: data as IAConversationProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to create conversation!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.AI_CONVERSATION.module],
    mutationFn: (payload?: Partial<IAConversationProps>) =>
      createConversation(payload),
  })
}

export const useAIUpdateConversation = () => {
  const updateConversation = async ({
    uuid,
    ...payload
  }: Partial<IAConversationProps>): Promise<{
    success: boolean
    message: string
    data?: IAConversationProps
  }> => {
    try {
      const { data } = await api.patch(AI_CONVERSATION_URL(uuid), payload)

      return {
        success: true,
        message: 'Conversation updated successfully!',
        data: data as IAConversationProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to update conversation!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.AI_CONVERSATION.module],
    mutationFn: (payload: Partial<IAConversationProps>) =>
      updateConversation(payload),
  })
}

export const useAIEvaluationConversation = () => {
  const evaluateConversation = async (
    uuid: string,
  ): Promise<{
    success: boolean
    message: string
    data?: undefined
  }> => {
    try {
      await api.get(AI_CONVERSATIONS_EVALUATION_URL(uuid))

      return {
        success: true,
        message: 'Conversation evaluated successfully!',
        data: undefined,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to evaluate conversation!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.AI_EVALUATION.module],
    mutationFn: (uuid: string) => evaluateConversation(uuid),
  })
}

export const useAIEvaluationAllConversations = ({
  params,
}: {
  params?: Record<string, string | number>
}) => {
  const evaluateAllConversations = async (): Promise<{
    success: boolean
    message: string
    data?: undefined
  }> => {
    try {
      const copyParams = { ...params }

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key]) {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      await api.get(AI_CONVERSATIONS_EVALUATION_ALL_URL, {
        params: {
          ...(formattedParams as Record<string, string>),
        },
      })

      return {
        success: true,
        message: 'Conversations evaluated successfully!',
        data: undefined,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to evaluate conversations!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.AI_EVALUATION.module],
    mutationFn: () => evaluateAllConversations(),
  })
}

export const useAIForceFinishedConversation = () => {
  const regenerateConversation = async (
    uuid: string,
    agent: string,
  ): Promise<{
    success: boolean
    message: string
    data?: undefined
  }> => {
    try {
      const { data } = await api.post(
        AI_CONVERSATIONS_FORCE_FINISHED_URL(uuid),
        {
          agent,
        },
      )

      return {
        success: true,
        message: 'Conversation regenerated successfully!',
        data: data?.agentResponse || '',
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to regenerate conversation!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.AI_REGENERATE.module],
    mutationFn: ({ uuid, agent }: { uuid: string; agent: string }) =>
      regenerateConversation(uuid, agent),
  })
}

export const useAIDeleteConversation = () => {
  const deleteConversation = async (
    uuid: string,
  ): Promise<{
    success: boolean
    message: string
  }> => {
    try {
      await api.delete(AI_CONVERSATION_URL(uuid))

      return {
        success: true,
        message: 'Conversation deleted successfully!',
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to delete conversation!',
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.AI_CONVERSATION.module],
    mutationFn: (uuid: string) => deleteConversation(uuid),
  })
}

export const useAIConversationMetrics = ({
  params,
}: {
  params?: Record<string, string | number>
}) => {
  const getConversationMetrics = async (): Promise<{
    success: boolean
    message: string
    data?: IAConversationMetricsProps
  }> => {
    const copyParams = { ...params }

    const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
      if (copyParams?.[key]) {
        return {
          ...acc,
          [key]: copyParams?.[key],
        }
      }

      return acc
    }, {})

    try {
      const { data } = await api.get(AI_CONVERSATION_METRICS_URL, {
        params: formattedParams,
      })

      return {
        success: true,
        message: 'Conversation metrics fetched successfully!',
        data: convertObjectToCamelCase(data) as IAConversationMetricsProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get conversation metrics!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.AI_CONVERSATION_METRICS.module, params],
    queryFn: getConversationMetrics,
  })
}

export interface IADashboardMonthlyMetricsProps {
  monthlyMetrics: {
    month: string
    totalConversations: number
    jtbdCompleted: number
    jtbdCompletionPercentage: number
    avgGeneralNote: number
  }[]
  agentJTBDStats: {
    agent: string
    totalEvaluations: number
    jtbdTrue: number
    jtbdFalse: number
  }[]
}

export const useAIDashboardMonthlyMetrics = (params: {
  dateAtAfter: string
  dateAtBefore: string
  role: string
}) => {
  const getDashboardMonthlyMetrics = async (): Promise<{
    success: boolean
    message: string
    data?: IADashboardMonthlyMetricsProps
  }> => {
    try {
      const copyParams = { ...params } as Record<string, string>

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key]) {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(AI_DASHBOARD_MONTHLY_METRICS_URL, {
        params: formattedParams,
      })

      return {
        success: true,
        message: 'Dashboard monthly metrics fetched successfully!',
        data: convertObjectToCamelCase(data) as IADashboardMonthlyMetricsProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get dashboard monthly metrics!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.AI_DASHBOARD_MONTHLY_METRICS.module, params],
    queryFn: getDashboardMonthlyMetrics,
  })
}

export const useAICreateConversationWithAgent = () => {
  const createConversation = async (payload?: {
    agentSlug: string
    phoneNumber: string
    message: string
  }): Promise<{
    success: boolean
    message: string
    data?: IAConversationProps
  }> => {
    try {
      const { data } = await api.post(AI_CONVERSATIONS_WITH_AGENT_URL, payload)

      return {
        success: true,
        message: 'Conversation created with agent successfully!',
        data: data as IAConversationProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to create conversation with agent!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.AI_CONVERSATION.module, 'with-agent'],
    mutationFn: (payload?: {
      agentSlug: string
      phoneNumber: string
      message: string
    }) => createConversation(payload),
  })
}
